/**
 * The sidebar's nav glyphs, drawn to the design comps for both portals.
 *
 * These replace the react-feather stand-ins the two `navigation.vertical.ts`
 * configs were using — each of those files carries a header note explaining
 * that the source app's per-item SVG pairs (`assets/images/icons/sidebar-menu/*`)
 * weren't part of the migration's asset set, so the nearest feather glyph stood
 * in. The comps draw a single, consistent Iconsax-linear family instead
 * (rounded 1.6px strokes on a 24px grid), which feather cannot express: there
 * is no feather receipt, no buildings pair, no profile-with-tick, and its
 * squarer 2px geometry reads as a different icon set next to the rest of the
 * design.
 *
 * Hand-authored inline SVG rather than a new icon dependency, following the
 * precedent already set by `components/icons.tsx` — same reasoning, one file up
 * in visibility because the app nav configs consume these through the barrel.
 *
 * Every glyph takes `size` (default 24, since the sidebar row draws them at 20)
 * and `className`, matching the `NavItem['icon']` contract in each app's
 * `navigation.types.ts`, so a config item swaps a feather import for one of
 * these with no other change.
 */
import type { SVGAttributes } from 'react';

export type SidebarIconProps = SVGAttributes<SVGSVGElement> & { size?: number };

function iconProps({ size = 24, ...props }: SidebarIconProps) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    ...props,
  };
}

/** Dashboard — four rounded tiles, two short and two tall, on a diagonal. */
export function DashboardIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <rect x="3" y="3" width="7.5" height="6.6" rx="2.1" />
      <rect x="13.5" y="3" width="7.5" height="10" rx="2.1" />
      <rect x="3" y="11.4" width="7.5" height="9.6" rx="2.1" />
      <rect x="13.5" y="14.4" width="7.5" height="6.6" rx="2.1" />
    </svg>
  );
}

/** Documents — a page whose top-right corner is folded back. */
export function DocumentsIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <path d="M14.4 3.5H8C5.2 3.5 3.5 5.2 3.5 8v8c0 2.8 1.7 4.5 4.5 4.5h8c2.8 0 4.5-1.7 4.5-4.5V9.6l-6.1-6.1Z" />
      <path d="M14.4 3.5v3.6c0 1.4 1.1 2.5 2.5 2.5h3.6" />
    </svg>
  );
}

/** Invoices — a torn-off receipt with a rolled tab at the top right. */
export function InvoiceIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <path d="M15.5 3.5H8A2.5 2.5 0 0 0 5.5 6v15.5l2 -1.5 2 1.5 2-1.5 2 1.5 2-1.5Z" />
      <path d="M15.5 3.5h1.6a2.4 2.4 0 0 1 2.4 2.4v2a2 2 0 0 1-2 2h-2" />
      <path d="M8.6 9h.01" />
      <path d="M11 9h2.4" />
      <path d="M8.6 12.6h.01" />
      <path d="M11 12.6h2.4" />
    </svg>
  );
}

/** Data consumers — a tall tower beside a low block of offices. */
export function BuildingsIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <path d="M3.5 21V6.2c0-1.7.9-2.7 2.6-2.7h3.1c.6 0 1.2.25 1.6.7l1.8 1.8c.45.45.7 1 .7 1.6V21" />
      <path d="M13.3 21v-8.2c0-1.4.8-2.2 2.2-2.2h3c1.4 0 2.2.8 2.2 2.2V21" />
      <path d="M2.6 21h18.8" />
      <path d="M6.8 7.6h2.6" />
      <path d="M6.8 11.1h2.6" />
      <rect x="15.1" y="13.2" width="1.7" height="1.7" rx=".5" />
      <rect x="17.8" y="13.2" width="1.7" height="1.7" rx=".5" />
      <path d="M15.1 21v-2.6c0-.6.45-1.05 1.05-1.05h2.3c.6 0 1.05.45 1.05 1.05V21" />
    </svg>
  );
}

/** Communication — a speech bubble with a second one answering behind it. */
export function MessagesIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <path d="M15.9 9.6c0 3.5-3.1 6.4-6.9 6.4-.45 0-.9-.04-1.33-.12l-2.98 1.77a.6.6 0 0 1-.9-.63l.45-2.45A6.2 6.2 0 0 1 2.1 9.6c0-3.53 3.1-6.4 6.9-6.4s6.9 2.87 6.9 6.4Z" />
      <path d="M18.4 12.9c2.05.9 3.5 2.7 3.5 4.8 0 1.2-.45 2.3-1.2 3.2l.36 1.95a.6.6 0 0 1-.9.63l-2.4-1.42c-.43.08-.88.12-1.33.12-2.55 0-4.8-1.3-5.9-3.25" />
      <path d="M6.2 9.6h.01" />
      <path d="M9 9.6h.01" />
      <path d="M11.8 9.6h.01" />
    </svg>
  );
}

/** Member list — a single profile with an approval tick. */
export function ProfileTickIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <circle cx="11.2" cy="7.1" r="3.6" />
      <path d="M13.4 19.3c-.7.13-1.44.2-2.2.2-3.4 0-6.2-1.55-6.2-3.5s2.8-3.5 6.2-3.5c1.5 0 2.9.3 4 .8" />
      <path d="m14.6 17.1 1.5 1.5 3.4-3.4" />
    </svg>
  );
}

/** Staff — a profile with a second one standing behind it. */
export function UsersIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <circle cx="9.3" cy="7.1" r="3.7" />
      <ellipse cx="9.3" cy="16.9" rx="5.8" ry="3.6" />
      <path d="M16.4 4.4a3.5 3.5 0 0 1 0 5.5" />
      <path d="M18.5 13.4c1.8.3 3 1.2 3 2.4 0 1.05-.9 1.9-2.3 2.3" />
    </svg>
  );
}

/** Usage report — a bar chart framed in a rounded square. */
export function ChartSquareIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <rect x="2.6" y="2.6" width="18.8" height="18.8" rx="5.6" />
      <path d="M8.2 16.4v-4.5" strokeWidth="2" />
      <path d="M12 16.4V7.6" strokeWidth="2" />
      <path d="M15.8 16.4v-2.6" strokeWidth="2" />
    </svg>
  );
}

/** Services & configs — the eight-lobed settings gear. */
export function SettingsGearIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <path d="M2.5 12.88v-1.76c0-1.04.85-1.9 1.9-1.9 1.81 0 2.55-1.28 1.64-2.85a1.9 1.9 0 0 1 .7-2.59l1.73-.99c.75-.45 1.72-.18 2.17.57l.11.19c.9 1.57 2.38 1.57 3.29 0l.11-.19c.45-.75 1.42-1.02 2.17-.57l1.73.99c.91.53 1.22 1.69.7 2.59-.91 1.57-.17 2.85 1.64 2.85 1.04 0 1.9.85 1.9 1.9v1.76c0 1.04-.85 1.9-1.9 1.9-1.81 0-2.55 1.28-1.64 2.85.53.91.21 2.06-.7 2.59l-1.73.99c-.75.45-1.72.18-2.17-.57l-.11-.19c-.9-1.57-2.38-1.57-3.29 0l-.11.19c-.45.75-1.42 1.02-2.17.57l-1.73-.99a1.9 1.9 0 0 1-.7-2.59c.91-1.57.17-2.85-1.64-2.85a1.9 1.9 0 0 1-1.9-1.9Z" />
      <circle cx="12" cy="12" r="2.9" />
    </svg>
  );
}

/** GDX onboarding / access requests — a letter sitting in an open envelope. */
export function InboxLetterIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <path d="M3.5 17.5v-4.9c0-.7.35-1.35.94-1.73L7.5 8.9V6.5c0-1.9 1.1-3 3-3h3c1.9 0 3 1.1 3 3v2.4l3.06 1.97c.59.38.94 1.03.94 1.73v4.9c0 1.9-1.1 3-3 3h-11c-1.9 0-3-1.1-3-3Z" />
      <path d="m3.6 12.4 7.3 3.7c.7.35 1.5.35 2.2 0l7.3-3.7" />
      <path d="M10 6.9h4" />
      <path d="M10.8 9.5h2.4" />
    </svg>
  );
}

/** Search listing — the comp's magnifier. */
export function SearchIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <circle cx="11" cy="11" r="7.5" />
      <path d="m20.5 20.5-2.6-2.6" />
    </svg>
  );
}

/**
 * Subscription — a payment card. The consumer comp has no subscription row
 * (the design draws GDX MarketPlace and Search Listing in that stretch of the
 * nav instead, neither of which exists in this app yet), so this is the one
 * glyph here drawn to the family rather than sampled from it.
 */
export function CardIcon(props: SidebarIconProps) {
  return (
    <svg {...iconProps(props)}>
      <rect x="2.5" y="4.8" width="19" height="14.4" rx="3.6" />
      <path d="M2.5 9.6h19" />
      <path d="M6.6 15.4h2.6" />
      <path d="M11.6 15.4h2.6" />
    </svg>
  );
}
